<template>
  <StatusCheck
    idName="Control Number"
    :getStatusFields="fieldList"
  >
    <template #not-found-error>
      <div>
        Make sure you entered the Control Number and PIN correctly.
      </div>
    </template>
    <template #footer>
      <br>
      <AlertMessage type='info' slim>
        <div class='fst-italic mt-3 text-muted statusinfobox'>
          What does the status of my request mean?
        </div>
        <h6>FOIA Request Received by Centers for Medicare &amp; Medicaid Services</h6>
        <p>
          CMS's Central Office Freedom of Information Group or Office of Program Operations and
          Local Engagement has received your FOIA or Medicare beneficiary claims (Privacy Act)
          request for agency records; the request has been entered into CMS's FOIA &amp; Privacy Act
          request tracking and processing management system.<br>
          <i>
            (Note: CMS's Office of Program Operations and Local Engagement plays an essential
            role in processing Medicare beneficiary claims requests.)
          </i>
        </p>
        <h6>
          FOIA Request Referred to Program Office(s) for Responsive Records Search and Fee Estimate
        </h6>
        <p>
          Processing of your FOIA or Privacy Act request is underway. Your request has been shared
          with the appropriate division (program office) within CMS to initiate the records search.
          If you submitted a request for Medicare beneficiary records, your request will be
          sent to one of CMS's Medicare Administrative Contractors (MACs) for a records search.
          In some instances, your request may need to be sent to multiple program offices and/or
          MACs, which can result in additional time needed to complete the full search. Depending
          on the type of requester and volume of the request, CMS may need to generate a fee
          estimate prior to proceeding with fully processing your request.<br>See
          <a href="https://www.archives.gov/files/ogis/assets/fees-at-a-glance-chart.pdf" target="_blank">
            https://www.archives.gov/files/ogis/assets/fees-at-a-glance-chart.pdf
          </a>.
        </p>
        <h6>
          FOIA Request Referred to Program Office(s) for Responsive Records Search, Fee Estimate
          and Direct Reply to Requester
        </h6>
        <p>
          Your request is in the same status as noted above; however, one of CMS's program offices
          (most often the Office of Program Operations and Local Engagement) or one of CMS's
          Medicare Administrative Contractors is authorized to release records directly to you
          without the need for CMS's Freedom of Information Group's review and FOIA disclosure
          analysis of records prior to release.
        </p>
        <h6>Request Processing Suspended - Pending Information From Requester </h6>
        <p>
          Processing your FOIA request may be suspended for two reasons:
          <ul>
            <li>
              Clarification may be needed for your request in order for CMS to complete an
              efficient and targeted search for responsive records.
            </li>
            <li>
              An advance payment fee may have been assessed in order to continue processing
              your current request.
            </li>
          </ul>
        </p>
        <h6>Interim Disclosure Determination Issued - Final Determination Pending</h6>
        <p>
          Part of your FOIA request has been processed; a portion of CMS agency records has been
          reviewed by a FOIA analyst and either a partial or full interim records release has been
          determined and issued, or a full denial of records reviewed thus far has been determined
          per FOIA statute exemptions. The remainder of your request is continuing to be processed.
        </p>
        <h6>Responsive Records Returned to the FOIA Office for Review and Disclosure Analysis</h6>
        <p>
          The CMS program office(s) assigned to your request has completed their search for
          responsive records and returned them to CMS's Freedom of Information Group for review
          and FOIA disclosure analysis.<br>
          <i>
            (Note: if your request was assigned to multiple program offices for search, the
            request may still be pending records production or response from other program
            offices.)
          </i>
        </p>
        <h6>Disclosure Analysis of Responsive Records in Progress</h6>
        <p>
          Agency records produced by the program office(s) have been received by CMS's Freedom of
          Information Group, the case has been assigned to a FOIA analyst, and disclosure
          analysis of responsive records has begun.
        </p>
        <h6>Disclosure Decision Pending</h6>
        <p>
          A FOIA analyst has completed the disclosure analysis of agency records and made a
          disclosure determination to issue either a partial or full release of records, or
          a full denial based on FOIA statute exemptions. Release or response is pending final
          review and approval from CMS's Freedom of Information Group leadership.
        </p>
        <h6>In Appeal Process</h6>
        <p>
          CMS has received your FOIA appeal request in response to a perceived adverse
          determination or failure to produce agency records; the appeal is under initial review
          or currently in appeal proceedings.
        </p>
        <h6>Appeal Case Closed</h6>
        <p>
          An appeal determination has been made and a final response has been sent; additional
          records may have been released as well. Both the original FOIA request and subsequent
          appeal are now closed.
        </p>
        <h6>Disclosure Decision Issued - Case Closed</h6>
        <p>
          Your FOIA request has been fully processed and a disclosure decision issued; either a
          partial or full set of agency records has been released or a full denial of records
          response has been issued per FOIA statute exemptions. Your FOIA request is now completed
          and closed.
        </p>
        <h6>Case Closed</h6>
        <p>
          Your FOIA request has been reviewed and closed administratively for one of several
          reasons:
          <ul>
            <li>CMS does not possess any records responsive to your request.</li>
            <li>
              Your request was withdrawn or a Continued Interest Letter was sent to you and
              no response was received.
            </li>
            <li>
              Requested records were not reasonably described and a clarification response
              was not received by CMS.
            </li>
            <li>
              All records in CMS's possession were referred to another agency for their
              review and release determination.
            </li>
            <li>
              Your request was not an actual FOIA request as defined by the FOIA statute,
              or it was imperfect and CMS did not receive a response from you to perfect
              the request in order to be submitted under FOIA.
            </li>
            <li>
              The records you requested are not CMS agency records.
            </li>
            <li>
              CMS's FOIA tracking and processing system received your request more than once
              and other request(s) were closed as a duplicate(s).
            </li>
            <li>
              If you submitted multiple FOIA requests, they may have been consolidated
              (with your permission) into fewer requests or a single request; therefore,
              other requests entered into CMS's FOIA tracking and processing system were closed.
            </li>
            <li>
              CMS's Freedom of Information Group was unable to contact you with questions and
              answers necessary to fully process your request as originally submitted.
            </li>
          </ul>
        </p>
      </AlertMessage>
    </template>
  </StatusCheck>
</template>

<script>
import AlertMessage from '@/components/AlertMessage';
import StatusCheck from '@/views/StatusCheck';
import { formatJsonDate } from '@/helpers/dateHelpers';

export default {
  components: {
    AlertMessage, StatusCheck
  },

  methods: {
    fieldList: data =>
      [
        ['Control Number', data.controlNumber],
        ['Date Received', formatJsonDate(data.dateReceived)],
        ['Subject', data.subject],
        ['Status of the Request', data.status],
        ['Projected Date of Response', 'Undetermined'],
        ['Date of Response', data.dateOfResponse ? formatJsonDate(data.dateOfResponse) : 'Undetermined']
      ]
  }
};
</script>

<style lang="css" scoped>
.statusinfobox {
  margin: 0px 0px 10px 0px !important;
}
</style>
